import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import AdminLayout from "../../layout/admin";

// Component Imports
import GenericDataLayout from "../../components/custom/GenericDataLayout";
import GenericTable from "../../components/custom/GenericTable";
import { getMethodApiCall } from "src/services/api";

const OjtVisits = () => {
  const { token } = useAuth();
  
  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  
  // Dropdown Data
  const [districts, setDistricts] = useState([]);
  const [itis, setItis] = useState([]);

  // Pagination & Filters State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    district: "",
    iti_name: "",
  });

  // --- Column Configuration ---
  const columns = [
    { header: "Industry Name", accessor: "industry_name" },
    { header: "District", accessor: "district" },
    { header: "ITI Name", accessor: "iti_name" },
    { 
      header: "Visit Date", 
      accessor: "visit_date",
      render: (row) => {
        if (!row.visit_date) return "-";
        // Simple date formatting
        return new Date(row.visit_date).toLocaleDateString();
      }
    },
    { 
      header: "Status", 
      accessor: "status",
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          row.status === 'Completed' ? 'bg-green-100 text-green-800' : 
          row.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 
          'bg-gray-100 text-gray-800'
        }`}>
          {row.status || "-"}
        </span>
      )
    }
  ];

  // --- API Calls ---

  const fetchDistrictsAndITIs = async () => {
    try {
      const res = await getMethodApiCall(`/v1/api/trainee/get_district_picklist`);
      const resData = res.data;
      if (resData.success) {
        setDistricts(resData?.data || []);  
        setItis([]);
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchData = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      // Pagination
      queryParams.append("page", pagination.page.toString());
      queryParams.append("limit", pagination.limit.toString());

      // Filters
      if (filters.district) {
        queryParams.append("district", filters.district.trim());
      }
      if (filters.iti_name) {
        queryParams.append("iti_name", filters.iti_name.trim());
      }

      const response = await getMethodApiCall(`/v1/api/admin/ojt-visits?${queryParams}`);

      const responseData = response.data;

      // FIX: Removed the check for 'response.data' which is likely undefined on the fetch Response object
      if (response.status === 200 && responseData.success) {
        setData(responseData.data || []); 
        setPagination((prev) => ({
          ...prev,
          totalRecords: responseData.total || 0, // Ensure API returns 'total' or 'pagination.total'
          totalPages: responseData.totalPages || 1,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch OJT visits data", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    if (token) {
      fetchDistrictsAndITIs();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pagination.page, pagination.limit, filters]);

  // --- Handlers ---

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({ district: "", iti_name: "" });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // --- Layout Configuration ---

  const filterConfig = [
    {
      type: "select",
      label: "District",
      value: filters.district,
      options: districts.map((d) => ({
        label: d.district,
        value: d.district,
      })),
      onChange: (val) => handleFilterChange("district", val),
    },
    {
      type: "select",
      label: "ITI Name",
      value: filters.iti_name,
      options: itis.map((i) => ({
        label: i.iti_name,
        value: i.iti_name,
      })),
      onChange: (val) => handleFilterChange("iti_name", val),
    },
  ];
  // return <h1>Development in progress</h1>

  return (
    <AdminLayout>
      <GenericDataLayout
        title="OJT Visits"
        subtitle="Manage Industry Partner Visits"
        isLoading={loading}
        filters={filterConfig}
        onResetFilters={handleResetFilters}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable 
          columns={columns} 
          data={[]} 
          pagination={pagination}
        />
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default OjtVisits;